import React from 'react';
import useLazyImage from '../hooks/useLazyImage';

/**
 * Lazy loading image component optimized for the image grid
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for accessibility
 * @param {string} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {string} props.placeholder - Placeholder image or color
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.lazyOptions - Options for intersection observer
 * @returns {JSX.Element} Lazy loaded image component
 */
const LazyImage = ({ 
  src, 
  alt = '', 
  className = '', 
  style = {}, 
  placeholder = '', 
  onClick,
  lazyOptions = {},
  ...props 
}) => {
  const { imageSrc, isLoaded, isInView, imageRef, handleImageLoad, handleImageError } = useLazyImage(
    src, 
    placeholder, 
    lazyOptions
  );

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    transition: 'opacity 0.3s ease',
    opacity: isLoaded ? 1 : 0,
    ...style
  };

  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: placeholder || '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div ref={imageRef} style={containerStyle} className={className} onClick={onClick}>
      {/* Placeholder/Loading state */}
      {!isLoaded && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          {isInView && (
            <div style={{
              width: '24px',
              height: '24px',
              border: '2px solid rgba(0, 212, 255, 0.3)',
              borderTop: '2px solid rgba(0, 212, 255, 0.8)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </div>
      )}
      
      {/* Actual image */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          style={imageStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy" // Native lazy loading as fallback
          decoding="async"
          {...props}
        />
      )}
    </div>
  );
};

export default React.memo(LazyImage);